/* eslint-disable complexity */
import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import type { BuildEmailOptions } from '@adornis/mails/db/templates.js';
import { addMinutes, format } from 'date-fns';
import { html } from 'lit';
import { ParticipationState } from '../enums/enums.js';
import {
  buildConfirmMail,
  buildForgotPassword,
  buildLoginLink,
  buildOTP,
  buildProductCard,
  buildProductCardText,
  buildRegistrationsHTML,
  getAppointmentsForParticipation,
} from '../mails/mail-helpers.js';
import { isDefined, toBerlinDate } from '../util.js';
import { ColorDefinitions } from './ColorDefinitions.js';
import type { Einzelberatung } from './einzelberatung.js';
import { Institution } from './institution.js';
import type { Participation } from './Participation.js';
import type { Product } from './products.js';
import { SettingColorDefintionProp } from './settings.js';
import type { User } from './user.js';
import type { Workshop } from './workshop.js';

export enum MailTemplateType {
  NUTZER_TEILNAHME = 'Nutzer_Teilnahme',
  NUTZER_TEILNAHME_OHNE_ZERTIFIKAT = 'Nutzer_Teilnahme_ohne_Zertifikat',
  NUTZER_TEILNAHME_AUFGEHOBEN = 'Nutzer_Teilnahme_Aufgehoben',
  NUTZER_WORKSHOP_SET_TEILNAHME = 'Nutzer_Workshop_Set_Teilnahme',
  BESTAETIGUNG_WORKSHOP_REGISTRIERUNG = 'Bestätigung_Workshop_Registrierung',
  SPERRUNG = 'Sperrung_durch_Einrichtung',
  SPERRUNG_AUFGEHOBEN = 'Sperrung_aufgehoben',
  NACHRUECKPLATZ_VERFUEGBAR = 'Nachrückplatz_verfügbar',
  NACHRUECKPLATZ_ERHALTEN = 'Nachrückplatz_erhalten',
  WARTELISTE_VORRUECK = 'Warteliste_vorrueck',
  VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN = 'Veranstaltungseintragung_durch_Admin',
  ABMELDUNG = 'Abmeldung',
  ABMELDUNG_DURCH_EINRICHTUNG = 'Abmeldung_durch_Einrichtung',
  PRODUCT_CANCELED = 'Product_canceled',
  PRODUCT_DECANCELED = 'Product_Decanceled',
  ERINNERUNG = 'Erinnerung',
  ERINNERUNG_2 = 'zweite_Erinnerung',
  ERINNERUNG_APPOINTMENT = 'Erinnerung_Appointment',
  ERINNERUNG_APPOINTMENT_2 = 'zweite_Erinnerung_Appointment',
  MEETING_INFOS = 'Meeting Informationen',
  MEETING_INFOS_2 = 'Meeting Informationen 2',
  MEETING_INFOS_IN_PERSON = 'Meeting Informationen vor Ort',
  MEETING_INFOS_2_IN_PERSON = 'Meeting Informationen vor Ort 2',
  MEETING_INFOS_HYBRID = 'Meeting Informationen Hybrid',
  MEETING_INFOS_2_HYBRID = 'Meeting Informationen Hybrid 2',
  EINZELBERATUNG_MEETING_INFOS = 'Einzelberatung Meeting Informationen',
  LECTURER_WORKSHOP_REMINDER = 'lecturer_workshop_reminder',
  LECTURER_EVALUATION_MAIL = 'lecturer_evaluation_mail',
  OTP = 'OTP-Code',
  CHANGE_EMAIL = 'Change_Email',
  FORGOT_PASSWORD = 'Passwort vergessen',
  PRODUKT_LOESCHUNG = 'Produkt_Loeschung',
  PRODUCT_CHANGED = 'Product_Änderungen',
  INFO_NEWSLETTER = 'INFO_NEWSLETTER',
  NEWSLETTER_OPTIN = 'Newsletter_Opt_In',
  FREE_FORM = 'FREE_FORM',
  IKH_INVOICE_USER = 'plugin::payment::ikh::IKH-Rechnung_User',
  IKH_INVOICE_INSTITUTION = 'plugin::payment::ikh::IKH-Rechnung_Institution',
  SD_INVOICE_USER = 'plugin::payment::sd::SD-Rechnung_User',
  SD_INVOICE_INSTITUTION = 'plugin::payment::sd::SD-Rechnung_Institution',
  DEMO_INVOICE_USER = 'plugin::payment::demo::IKH-Rechnung_User',
  DEMO_INVOICE_INSTITUTION = 'plugin::payment::demo::IKH-Rechnung_Institution',
  LECTURER_REGISTRATION = 'Lecturer_Registration',
  LECTURER_ADDED_TO_PRODUCT = 'Lecturer_Added_to_Workshop',
  NEW_WORKSHOP_DOCUMENTS = 'New_Workshop_Documents',
  NICHT_ERSCHIENEN = 'Nicht_Erschienen',
}

export enum MailTemplateVars {
  TITLE = 'title',
  FIRST_NAME = 'firstname',
  LAST_NAME = 'lastname',
  EMAIL = 'email',
  CHANGED_REGISTRATIONS = 'changed-registrations',
  CHANGED_REGISTRATIONS_EN = 'changed-registrations-en',
  OTHER_REGISTRATIONS = 'other-registrations',
  PRODUCT_CARD = 'termin-card',
  PRODUCT_CARD_EN = 'termin-card-en',
  APPOINTMENT_NAME = 'appointment-name',
  APPOINTMENT_START = 'appointment-start',
  APPOINTMENT_END = 'appointment-end',
  APPOINTMENT_LOCATION = 'appointment-location',
  LOGIN_LINK = 'login-link',
  OTP = 'otp-code',
  CONFIRM_MAIL_LINK = 'mail-confirm-link',
  FORGOT_PASSWORD_LINK = 'forgot-password-link',
  CHANGE_EMAIL_LINK = 'change-email-link',
  EVENT_TITLE = 'event-title',
  PRODUCT_NAME = 'product-name',
  EVENT_LOCATION = 'event-location',
  EVENT_DATES = 'event-dates',
  EVENT_LEAD_DAYS = 'event-lead-days', // (Calendar Days till start of Event/Appointment)
  INSTITUTION_NAME = 'institution-name',
  PRODUCT_CHANGES = 'product-changes',
  PRODUCT_CHANGES_EN = 'product-changes-en',
  IKH_INVOICE_USER = 'ikh-invoice-User',
  IKH_INVOICE_INSTITUTION = 'ikh-invoice-institution',
  SD_INVOICE_USER = 'sd-invoice-User',
  SD_INVOICE_INSTITUTION = 'sd-invoice-institution',
  AUTOMATIC_MOVE_UP_DAYS = 'automatic-move-up-days',
  BLOCK_SIGNOFF_DAYS = 'block-signoff-days',
  WORKSHOP_NAME = 'workshop-name',
  WORKSHOP_TERMINE = 'workshop-termine',
  WORKSHOP_LOCATION = 'workshop-location',
  BOOKABLE_WORKSHOPS = 'bookable-workshops',
  BOOKABLE_WORKSHOPS_EN = 'bookable-workshops-en',
  UNSUBSCRIBE_FROM_NEWSLETTER_LINK = 'unsubscribe-from-newsletter-link',
  NEWSLETTER_OPTIN_LINK = 'newsletter-optin-link',
  NEWSLETTER_OPTIN_LINK_EN = 'newsletter-optin-link-en',
  DOCUMENT_LINKS = 'document-links',
  MEETING_LINK = 'meeting-link',
  MEETING_CODE = 'meeting-code',
  MEETING_PASSWORD = 'meeting-password',
  EVALUATION_LINK = 'evaluation-link',
  EVALUATION_CODE = 'evaluation-code',
  TIMEFRAME_NAME = 'timeframe-name',
  DEREGISTER_LINK = 'deregister-link',
  DEREGISTER_LINK_EN = 'deregister-link-en',
}

@Entity()
export class GlobalMailTemplate extends MailTemplate {
  static override _class = 'GlobalMailTemplate';

  static override buildMailOptions: Partial<BuildEmailOptions> = {
    title: 'Buchungsheld Benachrichtigungsmail',
    footer: html`
      <div class="section" style="margin-top: 20px; font-size: 0.8em;">
        <div style="width: 100%">
          <div><a style="text-decoration: none;" href=${`${A.absoluteUrl()}impressum`}>Impressum</a></div>
          <div><a style="text-decoration: none;" href=${`${A.absoluteUrl()}datenschutz`}>Datenschutz</a></div>
        </div>
      </div>
    `,
    colorConfig: {
      mainBorder: 'none',
      mainBack: DesignSystem.currentTheme.getValue().colors.neutral.shade(50),
      wrapperBack: 'transparent',
    },
  };

  @Field(type => Boolean, { default: v => v ?? true })
  emailNotification: Maybe<boolean>;

  @Field(type => Boolean, { default: v => v ?? false })
  whatsappNotification: Maybe<boolean>;
}

@Entity()
export class InstitutionMailTemplate extends GlobalMailTemplate {
  static override _class = 'InstitutionMailTemplate';

  static get allFields() {
    return {
      ...selectionSet(() => this, 2),
      institutionID: 1,
      institution: {
        name: 1,
        colorDefinitions: selectionSet(() => ColorDefinitions, 3),
        emailBannerImageID: 1,
        settings: {
          colorDefinitions: selectionSet(() => ColorDefinitions, 4),
          inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
        },
        globalSettings: { colorDefinitions: selectionSet(() => ColorDefinitions, 3) },
      },
      footer: 1,
    } as const;
  }

  @Field(type => ID) institutionID?: string;
  @DataLoadedField(type => Institution, root => root.institutionID)
  institution: Maybe<Institution>;

  static mapFirstNameVar(user: NarrowedEntityData<User, { firstName: 1 }>) {
    return user.firstName || '';
  }

  static mapLastNameVar(user: NarrowedEntityData<User, { lastName: 1 }>) {
    return user.lastName || '';
  }

  static mapTime(time: Maybe<Date>) {
    if (!time) return '-';
    return format(toBerlinDate(time), 'HH:mm');
  }

  /* @deprecated use getWorkshopTerminString instead */
  static workshopTerminStringFromEntity(workshop: NarrowedEntity<Workshop, { appointments: { start: 1; end: 1 } }>) {
    const terminStrings = workshop.appointments
      .map(a => {
        if (!a.start || !a.end) return null;
        return `${format(toBerlinDate(a.start), 'dd.MM.yyyy')} ${format(toBerlinDate(a.start), 'HH:mm')} - ${format(
          toBerlinDate(a.end),
          'HH:mm',
        )}`;
      })
      .filter(isDefined);

    if (!terminStrings.length) return '-';
    return terminStrings.join('; ');
  }

  static workshopTerminString(workshop: NarrowedEntityData<Workshop, { appointments: { start: 1; end: 1 } }>) {
    const terminStrings = workshop.appointments
      .map(a => {
        if (!a.start || !a.end) return null;
        return `${format(toBerlinDate(a.start), 'dd.MM.yyyy')} ${format(toBerlinDate(a.start), 'HH:mm')} - ${format(toBerlinDate(a.end), 'HH:mm')}`;
      })
      .filter(isDefined);

    if (!terminStrings.length) return '-';
    return terminStrings.join('; ');
  }

  static einzelberatungTerminString(
    product: NarrowedEntity<Einzelberatung, { slots: { start: 1; end: 1 } }>,
    timeslotID: string,
  ) {
    const slot = (product as Einzelberatung).slots.find(s => s._id === timeslotID);
    if (!slot?.start || !slot?.end) return '-';
    return `${format(toBerlinDate(slot.start), 'dd.MM.yyyy')} ${format(toBerlinDate(slot.start), 'HH:mm')} - ${format(toBerlinDate(slot.end), 'HH:mm')}`;
  }

  static mapEventTitle(t: Product) {
    return t.name || '';
  }

  /**
   * @deprecated use buildRegistrationsHTML directly
   */
  static mapChangedRegistrationVar(options: {
    productBefore: Maybe<NarrowedEntityData<Product, { name: 1; topic: 1; language: 1 }>>;
    newState: ParticipationState;
    newWaitingListPlace: Maybe<number>;
    participation?: NarrowedEntityData<
      Participation,
      {
        product: {
          _id: 1;
          _class: 1;
          slots: { _id: 1; start: 1; end: 1 };
          settings: { courseLocation: 1; inheritedCourseLocation: 1 };
          appointments: { start: 1; name: 1; end: 1; location: 1; meetingLink: { link: 1 } };
        };
        timeslotID: 1;
        appointments: { start: 1; name: 1; end: 1; location: 1 };
      }
    >;
    institution: NarrowedEntity<Institution, {}>;
    englishVersion: boolean;
  }) {
    return buildRegistrationsHTML(
      [
        {
          name: options.productBefore?.name ?? '',
          state: options.newState,
          topic: options.productBefore?.topic,
          language: options.productBefore?.language,
          waitingListPlace:
            options.newWaitingListPlace && options.newState === ParticipationState.waiting
              ? options.newWaitingListPlace
              : undefined,
          appointments: getAppointmentsForParticipation(options.participation),
          englishVersion: options.englishVersion,
        },
      ],
      true,
    );
  }

  /**
   * @deprecated use buildRegistrationsHTML directly
   */
  static mapOtherRegistrationsVar(options: {
    products: NarrowedEntityData<Product, { _id: 1; name: 1; price: 1; hasPrice: 1; topic: 1; language: 1 }>[];
    participations: NarrowedEntityData<Participation, { _id: 1; productID: 1; state: 1; waitingListPlace: 1 }>[];
    institution: NarrowedEntityData<Institution, {}>;
    englishVersion: boolean;
  }) {
    const participationData = options.participations
      .map(participation => {
        const product = options.products.find(product => product._id === participation.productID);
        const appointments = (product as Workshop)?.appointments ?? [];
        if (appointments.length === 0) return undefined;
        return {
          name: product?.name ?? '',
          state: participation.state,
          topic: product?.topic,
          language: product?.language,
          institution: options.institution,
          waitingListPlace:
            participation.waitingListPlace && participation.state === ParticipationState.waiting
              ? participation.waitingListPlace
              : undefined,
          appointments: appointments.map(a => ({
            name: a.name,
            location: a.location,
            start: a.start ?? null,
            end: a.end ?? null,
          })),
          englishVersion: options.englishVersion,
        };
      })
      .filter(isDefined);

    if (participationData.length === 0) {
      return '';
    }

    return buildRegistrationsHTML(participationData);
  }

  static mapProductVar(
    product: NarrowedEntityData<
      Product,
      { name: 1; _class: 1; slotConfig: { start: 1; end: 1 }; appointments: { start: 1; end: 1; location: 1; name: 1 } }
    >,
    lang: 'de' | 'en' = 'de',
  ) {
    if (product._class === 'Workshop') {
      const workshop = product as Workshop;
      const appointments = workshop.appointments ?? [];
      if (appointments.length <= 1) {
        return buildProductCard(
          product.name,
          appointments[0]?.start,
          appointments[0]?.end,
          workshop.dateIsToBeDetermined,
          appointments[0]?.location,
          lang,
        );
      }
      return appointments
        .map((a, idx) => buildProductCard(a.name ?? `${idx + 1}. Termin`, a.start, a.end, false, a.location, lang))
        .join('');
    }
    if (product._class === 'Einzelberatung') {
      const slotConfig = (product as Einzelberatung).slotConfig!;
      return buildProductCard(product.name, slotConfig.start, slotConfig.end, false, null, lang);
    }
    throw new Error('unreachable');
  }

  static mapProductVarEinzelberatung(einzelberatung: Einzelberatung, slot: Date, lang: 'de' | 'en' = 'de') {
    return buildProductCard(
      einzelberatung.name,
      slot,
      addMinutes(slot, einzelberatung.slotConfig?.slotDuration ?? 15),
      false,
      null,
      lang,
    );
  }

  static mapProductVarText(
    product: (
      | NarrowedEntityData<Workshop, { name: 1; _class: 1; appointments: { name: 1; start: 1; end: 1; location: 1 } }>
      | NarrowedEntityData<Einzelberatung, { name: 1; _class: 1; slotConfig: { start: 1; end: 1 } }>
    ) & { dateIsToBeDetermined?: boolean },
    lang: 'de' | 'en' = 'de',
  ): string {
    if (product._class === 'Workshop' && 'appointments' in product) {
      const appointments = product.appointments ?? [];
      return buildProductCardText({
        name: product.name,
        entries: appointments.map((appointment, index) => ({
          label: appointments.length > 1 ? (appointment.name ?? `${index + 1}. Termin`) : undefined,
          start: appointment.start,
          end: appointment.end,
          location: appointment.location,
        })),
        notYetDetermined: product.dateIsToBeDetermined,
        lang,
      });
    }
    if (product._class === 'Einzelberatung' && 'slotConfig' in product) {
      return buildProductCardText({
        name: product.name,
        entries: [{ start: product.slotConfig?.start, end: product.slotConfig?.end }],
        notYetDetermined: product.dateIsToBeDetermined,
        lang,
      });
    }
    throw new Error('unreachable');
  }

  static mapProductVarEinzelberatungText(
    einzelberatung: NarrowedEntityData<Einzelberatung, { name: 1 }> &
      Partial<NarrowedEntityData<Einzelberatung, { slotConfig: { slotDuration: 1 } }>> & {
        dateIsToBeDetermined?: boolean;
      },
    slot: Date,
    lang: 'de' | 'en' = 'de',
  ): string {
    return buildProductCardText({
      name: einzelberatung.name,
      entries: [{ start: slot, end: addMinutes(slot, einzelberatung.slotConfig?.slotDuration ?? 15) }],
      notYetDetermined: einzelberatung.dateIsToBeDetermined,
      lang,
    });
  }

  static mapLoginLinkVar(token: string) {
    return buildLoginLink(token);
  }

  static mapOTPVar(code: string) {
    return buildOTP(code);
  }

  static mapConfirmMailVar(link: string) {
    return buildConfirmMail(link);
  }

  static mapForgotPassword(link: string) {
    return buildForgotPassword(link);
  }
}

export type WhatsAppTemplate = {
  name: string;
  bodyParams?: string[];
  bodyParamCount?: number;
  expectedHeader?: 'IMAGE' | 'DOCUMENT' | null;
  urlButton?: 'root' | 'dynamic';
};

export function getWhatsappBodyParamCount(template: WhatsAppTemplate): number | undefined {
  return template.bodyParams?.length ?? template.bodyParamCount;
}

function whatsappTemplateWithBodyParams(
  name: string,
  bodyParams: string[],
  expectedHeader?: WhatsAppTemplate['expectedHeader'],
  options?: { urlButton?: WhatsAppTemplate['urlButton'] },
): WhatsAppTemplate {
  return { name, bodyParams, bodyParamCount: bodyParams.length, expectedHeader, ...options };
}

export function getWhatsappTemplate(tempalte: MailTemplateType): WhatsAppTemplate {
  switch (tempalte) {
    case MailTemplateType.ABMELDUNG:
      return whatsappTemplateWithBodyParams('abmeldung', ['Vorname', 'Nachname', 'Produktname'], null);
    case MailTemplateType.ABMELDUNG_DURCH_EINRICHTUNG:
      return whatsappTemplateWithBodyParams(
        'abmeldung_durch_einrichtung',
        ['Vorname', 'Nachname', 'Produktname'],
        null,
      );
    case MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG:
      return whatsappTemplateWithBodyParams(
        'bestaetigung_workshop_registrierung',
        ['Vorname', 'Nachname', 'Produktnamen'],
        null,
      );
    case MailTemplateType.PRODUCT_CANCELED:
      return whatsappTemplateWithBodyParams('product_canceled', ['Vorname', 'Nachname', 'Produktname'], null);
    case MailTemplateType.PRODUCT_DECANCELED:
      return whatsappTemplateWithBodyParams('product_decanceled', ['Vorname', 'Nachname', 'Produktname'], null);
    case MailTemplateType.ERINNERUNG:
      return whatsappTemplateWithBodyParams(
        'erinnerung',
        ['Vorname', 'Nachname', 'Produktname', 'Termine', 'Veranstaltungsort'],
        null,
        { urlButton: 'root' },
      );
    case MailTemplateType.ERINNERUNG_2:
      return whatsappTemplateWithBodyParams(
        'erinnerung_2',
        ['Vorname', 'Nachname', 'Produktname', 'Termine', 'Veranstaltungsort'],
        null,
      );
    case MailTemplateType.ERINNERUNG_APPOINTMENT:
      return whatsappTemplateWithBodyParams(
        'erinnerung_appointment',
        ['Vorname', 'Nachname', 'Produktname', 'Termin', 'Veranstaltungskarte', 'Abmeldelink'],
        null,
      );
    case MailTemplateType.ERINNERUNG_APPOINTMENT_2:
      return whatsappTemplateWithBodyParams(
        'erinnerung_appointment_2',
        ['Vorname', 'Nachname', 'Produktname', 'Termin', 'Veranstaltungskarte', 'Abmeldelink'],
        null,
      );
    case MailTemplateType.MEETING_INFOS:
      return whatsappTemplateWithBodyParams(
        'meeting_infos_online',
        ['Vorname', 'Nachname', 'Produktname', 'Termin', 'Meeting-Link', 'Veranstaltungskarte'],
        null,
      );
    case MailTemplateType.MEETING_INFOS_2:
      return whatsappTemplateWithBodyParams(
        'meeting_infos_online_2',
        ['Vorname', 'Nachname', 'Produktname', 'Termin', 'Meeting-Link', 'Veranstaltungskarte'],
        null,
      );
    case MailTemplateType.MEETING_INFOS_IN_PERSON:
      return whatsappTemplateWithBodyParams(
        'meeting_infos_vor_ort',
        ['Vorname', 'Nachname', 'Produktname', 'Termin', 'Ort', 'Veranstaltungskarte'],
        null,
      );
    case MailTemplateType.MEETING_INFOS_2_IN_PERSON:
      return whatsappTemplateWithBodyParams(
        'meeting_infos_vor_ort_2',
        ['Vorname', 'Nachname', 'Produktname', 'Termin', 'Ort', 'Veranstaltungskarte'],
        null,
      );
    case MailTemplateType.MEETING_INFOS_HYBRID:
      return whatsappTemplateWithBodyParams(
        'meeting_infos_hybrid',
        ['Vorname', 'Nachname', 'Produktname', 'Termin', 'Ort', 'Meeting-Link', 'Veranstaltungskarte'],
        null,
      );
    case MailTemplateType.MEETING_INFOS_2_HYBRID:
      return whatsappTemplateWithBodyParams(
        'meeting_infos_hybrid_2',
        ['Vorname', 'Nachname', 'Produktname', 'Termin', 'Ort', 'Meeting-Link', 'Veranstaltungskarte'],
        null,
      );
    case MailTemplateType.EINZELBERATUNG_MEETING_INFOS:
      return { name: 'einzelberatung_meeting_infos' };
    case MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR:
      return whatsappTemplateWithBodyParams('nachrckplatz_verfuegbar', ['Vorname', 'Nachname', 'Produktnamen'], null);
    case MailTemplateType.NACHRUECKPLATZ_ERHALTEN:
      return whatsappTemplateWithBodyParams(
        'nachrckplatz_erhalten2',
        ['Vorname', 'Nachname', 'Produktnamen', 'Termin'],
        null,
      );
    case MailTemplateType.NUTZER_TEILNAHME:
      return whatsappTemplateWithBodyParams('nutzer_teilnahme', ['Vorname', 'Nachname', 'Produktname'], 'DOCUMENT');
    case MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT:
      return whatsappTemplateWithBodyParams(
        'nutzer_teilnahme_no_attachment',
        ['Vorname', 'Nachname', 'Produktname'],
        null,
      );
    case MailTemplateType.SPERRUNG:
      return whatsappTemplateWithBodyParams('sperrung_durch_einrichtung', ['Vorname', 'Nachname'], null);
    case MailTemplateType.SPERRUNG_AUFGEHOBEN:
      return whatsappTemplateWithBodyParams('sperrung_aufgehoben', ['Vorname', 'Nachname'], null);
    case MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN:
      return whatsappTemplateWithBodyParams(
        'veranstaltungseintragung_durch_admin',
        ['Vorname', 'Nachname', 'Produktname', 'Termin'],
        null,
      );
    case MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN:
      return whatsappTemplateWithBodyParams(
        'nutzer_teilnahme_aufgehoben',
        ['Vorname', 'Nachname', 'Produktname'],
        null,
      );
    case MailTemplateType.OTP:
      return whatsappTemplateWithBodyParams('otp_code', ['Einmalcode'], null, { urlButton: 'dynamic' });
    case MailTemplateType.WARTELISTE_VORRUECK:
      return whatsappTemplateWithBodyParams('warteliste_vorrueck', [
        'Titel',
        'Vorname',
        'Nachname',
        'Produktname',
        'Wartelistenplatz',
      ]);
    case MailTemplateType.PRODUKT_LOESCHUNG:
      return whatsappTemplateWithBodyParams('produkt_loeschung', ['Vorname', 'Nachname', 'Produktname'], null);
    case MailTemplateType.PRODUCT_CHANGED:
      return whatsappTemplateWithBodyParams('product_anderungen', ['Vorname', 'Nachname', 'Produktname'], null);
    case MailTemplateType.IKH_INVOICE_USER:
    case MailTemplateType.SD_INVOICE_USER:
      return whatsappTemplateWithBodyParams('ikh_invoice_user', ['Vorname', 'Nachname'], 'IMAGE');
    case MailTemplateType.IKH_INVOICE_INSTITUTION:
    case MailTemplateType.SD_INVOICE_INSTITUTION:
      return whatsappTemplateWithBodyParams('ikh_institution_invoice', ['Vorname', 'Nachname'], 'IMAGE');
    case MailTemplateType.NEW_WORKSHOP_DOCUMENTS:
    case MailTemplateType.NEWSLETTER_OPTIN:
      throw new Error('not implemented');
    default:
      throw new Error('not implemented');
  }
}

export function getAllRequiredWhatsappTemplateNames(): {
  mailTemplateType: string;
  templateName: string;
  bodyParams?: string[];
  bodyParamCount?: number;
  expectedHeader?: 'IMAGE' | 'DOCUMENT' | null;
  urlButton?: 'root' | 'dynamic';
}[] {
  return Object.values(MailTemplateType).flatMap(mailTemplateType => {
    try {
      const { name: templateName, ...metadata } = getWhatsappTemplate(mailTemplateType);
      return [{ mailTemplateType, templateName, ...metadata }];
    } catch (error) {
      if (error instanceof Error && error.message === 'not implemented') return [];
      throw error;
    }
  });
}
